import styled from "styled-components";
import { Button } from "../../common/generic/Button";
import { GenericModal } from "../Styles";

/* Modal Container */
export const StyledModal = styled(GenericModal)`
    max-width: 600px;

    .ant-modal-body {
        padding: 24px;
    }

    .ant-modal-footer {
        display: none;
    }
`;

/* Form Layout Components */
export const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

export const ValidationContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-end;
`;

export const ValidationButton = styled(Button)`
    min-width: 80px;
    height: 32px;
`;

export const ButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 24px;
`;

/* Status and Validation Components */
export const StatusMessage = styled.div<{ isError?: boolean }>`
    border-radius: 4px;
    background-color: ${props => props.isError ? props.theme.errorBackground : props.theme.successBackground};
    color: ${props => props.isError ? props.theme.errorText : props.theme.successText};
    margin-top: 16px;
    font-size: 14px;
`;

export const ValidationStatus = styled.div<{ isValid?: boolean; isError?: boolean }>`
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    margin-top: 4px;
    background-color: ${props =>
        props.isError ? props.theme.errorBackground :
        props.isValid ? props.theme.successBackground :
        props.theme.infoBackground};
    color: ${props =>
        props.isError ? props.theme.errorText :
        props.isValid ? props.theme.successText :
        props.theme.infoText};
`;

/* Club ID Display Components */
export const ClubIdContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    border-radius: 8px;
    background-color: ${props => props.theme.backgroundSecondary};
    border: 2px solid ${props => props.theme.successBorder || "#52c41a"};
    margin-top: 16px;
`;

export const ClubIdLabel = styled.div`
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.theme.foregroundMain};
    margin-bottom: 4px;
`;

export const ClubIdDisplay = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border-radius: 4px;
    background-color: ${props => props.theme.backgroundMain};
    border: 1px solid ${props => props.theme.borderSecondary || "#d9d9d9"};
`;

export const ClubIdText = styled.div`
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    font-size: 16px;
    font-weight: 600;
    color: ${props => props.theme.foregroundMain};
    flex: 1;
    user-select: all;
    cursor: text;
    letter-spacing: 0.5px;
`;

export const CopyButton = styled(Button)`
    min-width: 80px;
    height: 32px;
    font-size: 12px;
`;

export const CopyFeedback = styled.div<{ isVisible: boolean }>`
    font-size: 12px;
    color: ${props => props.theme.successText};
    opacity: ${props => props.isVisible ? 1 : 0};
    transition: opacity 0.3s ease;
    margin-top: 4px;
`;

/* Progress Components */
export const ProgressContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    border-radius: 4px;
    background-color: ${props => props.theme.backgroundSecondary};
`;

export const ProgressStep = styled.div<{ isActive?: boolean; isComplete?: boolean; isError?: boolean }>`
    display: flex;
    align-items: center;
    border-radius: 4px;
    font-size: 14px;
    background-color: ${props =>
        props.isError ? props.theme.errorBackground :
        props.isComplete ? props.theme.successBackground :
        props.isActive ? props.theme.infoBackground :
        "transparent"};
    color: ${props =>
        props.isError ? props.theme.errorText :
        props.isComplete ? props.theme.successText :
        props.isActive ? props.theme.infoText :
        props.theme.foregroundSecondary};
    opacity: ${props => props.isActive || props.isComplete || props.isError ? 1 : 0.6};
`;

export const Label = styled.label`
    color: ${props => props.theme.foregroundMain};
    font-weight: 500;
    margin-bottom: 8px;
    display: block;
`;

/* Title Info Card Components */
export const TitleInfoCard = styled.div`
    display: flex;
    gap: 16px;
    padding: 16px;
    border-radius: 8px;
    background-color: ${props => props.theme.backgroundSecondary};
    border: 2px solid ${props => props.theme.successBorder || "#52c41a"};
    margin-top: 12px;
`;

export const TitleImage = styled.img`
    width: 80px;
    height: 80px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
`;

export const TitleImagePlaceholder = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 8px;
    background-color: ${props => props.theme.backgroundTertiary || "#f0f0f0"};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.foregroundSecondary};
    font-size: 12px;
    text-align: center;
    flex-shrink: 0;
`;

export const TitleDetails = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const TitleName = styled.h3`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.theme.foregroundMain};
`;

export const TitleMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const TitleMetaItem = styled.div`
    font-size: 14px;
    color: ${props => props.theme.foregroundSecondary};

    strong {
        color: ${props => props.theme.foregroundMain};
    }
`;

export const TitleDescription = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${props => props.theme.foregroundSecondary};
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

/* Gamertag Validation Card Components */
export const GamertagValidationCard = styled.div`
    display: flex;
    gap: 16px;
    padding: 16px;
    border-radius: 8px;
    background-color: ${props => props.theme.backgroundSecondary};
    border: 2px solid ${props => props.theme.successBorder || "#52c41a"};
    margin-top: 12px;
`;

export const DisplayPic = styled.img`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
`;

export const DisplayPicPlaceholder = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: ${props => props.theme.backgroundTertiary || "#f0f0f0"};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.foregroundSecondary};
    font-size: 12px;
    text-align: center;
    flex-shrink: 0;
`;

export const GamertagInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const GamertagName = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: ${props => props.theme.foregroundMain};
`;

export const XuidInfo = styled.div`
    font-size: 14px;
    color: ${props => props.theme.foregroundSecondary};

    strong {
        color: ${props => props.theme.foregroundMain};
    }
`;

/* Bold Text Components */
export const BoldText = styled.span`
    color: ${props => props.theme.foregroundMain};
    font-weight: 600;
`;

/* Flow Selection Components */
export const FlowSelectorContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px 0;
`;

export const FlowSelectorTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.theme.foregroundMain};
    text-align: center;
`;

export const FlowOptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const FlowOption = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    border-radius: 8px;
    background-color: ${props => props.theme.backgroundSecondary};
    border: 2px solid ${props => props.theme.borderSecondary || "#d9d9d9"};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${props => props.theme.primaryColor || "#1890ff"};
        background-color: ${props => props.theme.backgroundTertiary || "#f5f5f5"};
    }

    &:active {
        transform: translateY(1px);
    }
`;

export const FlowIcon = styled.div`
    font-size: 32px;
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background-color: ${props => props.theme.backgroundMain};
    border: 1px solid ${props => props.theme.borderSecondary || "#d9d9d9"};
`;

export const FlowOptionContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const FlowOptionTitle = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${props => props.theme.foregroundMain};
`;

export const FlowOptionDescription = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${props => props.theme.foregroundSecondary};
    line-height: 1.4;
`;

/* Flow Container Components */
export const FlowContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

export const FlowHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${props => props.theme.borderSecondary || "#d9d9d9"};
`;

export const BackButton = styled.button`
    background: none;
    border: none;
    color: ${props => props.theme.primaryColor || "#1890ff"};
    font-size: 14px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: ${props => props.theme.backgroundSecondary};
    }

    &:focus {
        outline: 2px solid ${props => props.theme.primaryColor || "#1890ff"};
        outline-offset: 2px;
    }
`;

export const FlowTitle = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: ${props => props.theme.foregroundMain};
    flex: 1;
`;

/* Club Validation Card Components */
export const ClubValidationCard = styled.div`
    display: flex;
    gap: 16px;
    padding: 16px;
    border-radius: 8px;
    background-color: ${props => props.theme.backgroundSecondary};
    border: 2px solid ${props => props.theme.successBorder || "#52c41a"};
    margin-top: 12px;
`;

export const ClubPicture = styled.img`
    width: 80px;
    height: 80px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
`;

export const ClubPicturePlaceholder = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 8px;
    background-color: ${props => props.theme.backgroundTertiary || "#f0f0f0"};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.foregroundSecondary};
    font-size: 12px;
    text-align: center;
    flex-shrink: 0;
`;

export const ClubInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const ClubName = styled.h3`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.theme.foregroundMain};
`;

export const ClubOwner = styled.div`
    font-size: 14px;
    color: ${props => props.theme.foregroundSecondary};

    strong {
        color: ${props => props.theme.foregroundMain};
    }
`;

export const ClubDescription = styled.div`
    font-size: 12px;
    color: #666;
    margin: 4px 0;
    font-style: italic;
    max-width: 100%;
    word-wrap: break-word;
`;

export const OwnerSection = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
`;

export const OwnerDisplayPic = styled.img`
    width: 32px;
    height: 32px;
    border-radius: 16px;
    object-fit: cover;
`;

export const OwnerDisplayPicPlaceholder = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 16px;
    background-color: #e0e0e0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 8px;
    color: #666;
    text-align: center;
`;

export const OwnerInfo = styled.div`
    font-size: 12px;
    color: #333;
`;

export const ClubIdInfo = styled.div`
    font-size: 14px;
    color: ${props => props.theme.foregroundSecondary};
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;

    strong {
        color: ${props => props.theme.foregroundMain};
    }
`;
